import {
  Fragment,
  FunctionComponent,
  MouseEventHandler,
  MutableRefObject,
  useCallback,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useMatch } from 'react-router-dom';
import { Appearance, DraftDesignConfiguration, Size, ViewMap } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { getStickerImageUrl } from '@/utils/imageUrls/getStickerImageUrl';
import { isInches } from '@/utils/params';
import { getMouseFallbackListener, getPointerListener } from '@/utils/pointerEvents';
import { STICKER_SIZE_ROUTE } from '@/hooks/useStickerSize';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { Delete } from '@/components/common/icons/Delete';
import { Lovetab } from '@/components/common/icons/Lovetab';
import { useDraftDispatch } from '@/components/draft/DraftContext';
import { removeConfigurationAction } from '../../draftState/actions/removeConfigurationAction';
import { useConfigurationSelector } from '../../hooks/useConfigurationSelector';
import { useScaleFactor } from '../../hooks/useScaleFactor';
import { getLocalizedSize } from '../../utils/getLocalizedSize';
import { ConfigurationHandle } from '../ConfigurationHandle';
import { RendererProps } from '../ConfigurationRenderer';
import './StickerDesignRenderer.scss';

const getDesignImageAttributes = (
  {
    design: {
      size: { width: designWidth, height: designHeight },
    },
  }: DraftDesignConfiguration,
  viewMap: ViewMap,
  viewWidth: number,
  viewHeight: number
) => {
  let x = 0;
  let y = 0;

  const scaleX = viewWidth / designWidth;
  const scaleY = viewHeight / designHeight;

  let width: number;
  let height: number;
  if (scaleY < scaleX) {
    height = viewHeight;
    width = designWidth * scaleY;
    x += (viewWidth - width) / 2;
  } else {
    width = viewWidth;
    height = designHeight * scaleX;
    y += (viewHeight - height) / 2;
  }

  return [x, y, width, height] as const;
};

interface StickerSizeGridProps {
  sizeDesign: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const StickerSizeGrid: FunctionComponent<StickerSizeGridProps> = ({
  sizeDesign,
  x,
  y,
  width,
  height,
}) => {
  let gridWidth = (10 * Math.max(width, height)) / sizeDesign;

  // show grid in inch size for UK and NA countries
  if (isInches()) {
    gridWidth *= 2.54;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <defs>
        <pattern id="grid" width={gridWidth} height={gridWidth} patternUnits="userSpaceOnUse">
          <path d={`M0 ${gridWidth}V0H${gridWidth}`} fill="none" stroke="gray" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width={width} height={height} fill="url(#grid)" />
      <path d={`M0 ${height}H${width}V0`} fill="none" stroke="gray" strokeWidth="0.5" />
    </g>
  );
};

interface StickerSizeInfoProps {
  container?: MutableRefObject<HTMLElement | null>;
  designSize: Size;
}

const StickerSizeInfo: FunctionComponent<StickerSizeInfoProps> = ({ container, designSize }) => {
  if (container?.current) {
    return createPortal(
      <p className="draft-viewer__info">
        {getLocalizedSize(designSize.width)} x {getLocalizedSize(designSize.height)}
      </p>,
      container.current
    );
  }

  return null;
};

interface ReadOnlyStickerProps {
  imgUrl: string;
  width: number;
  height: number;
}

const ReadOnlySticker: FunctionComponent<ReadOnlyStickerProps> = ({ imgUrl, width, height }) => (
  <image key={imgUrl} xlinkHref={imgUrl} width={width} height={height} />
);

interface StickerProps {
  configuration: DraftDesignConfiguration;
  imgUrl: string;
  viewMap: ViewMap;
  viewWidth: number;
  viewHeight: number;
  isSelected: boolean;
  container?: MutableRefObject<HTMLElement | null>;
}

const Sticker: FunctionComponent<StickerProps> = ({
  configuration,
  imgUrl,
  viewMap,
  viewWidth,
  viewHeight,
  isSelected,
  container,
}) => {
  const dispatch = useDraftDispatch();
  const isStickerSizePanelActive = Boolean(useMatch(STICKER_SIZE_ROUTE));
  const [showLoading, setShowLoading] = useState(true);

  const [targetRef, selectConfiguration] = useConfigurationSelector<SVGGElement>(
    configuration,
    isSelected
  );

  const handleDelete: MouseEventHandler<SVGGElement> = () => {
    trackFeature('delete-configuration');
    dispatch(removeConfigurationAction(configuration));
  };

  const designSize = getCurrentSizeOfConfiguration(configuration);

  const [setScaleRef, scaleFactor] = useScaleFactor(configuration.printArea);
  const setRef = useCallback(
    (element: SVGGElement | null) => {
      targetRef.current = element;
      setScaleRef(element);
    },
    [targetRef, setScaleRef]
  );

  const [x, y, width, height] = getDesignImageAttributes(
    configuration,
    viewMap,
    viewWidth,
    viewHeight
  );

  const configSelectedOrOpenPanel = isSelected || isStickerSizePanelActive;

  if (showLoading) {
    return (
      <g ref={setScaleRef}>
        <Lovetab
          x={viewWidth / 4}
          y={viewHeight / 4}
          width={viewWidth / 2}
          height={viewHeight / 2}
          className="sticker__loader"
        />
        <image
          key={imgUrl}
          xlinkHref={imgUrl}
          width={0}
          height={0}
          // eslint-disable-next-line react/no-unknown-property
          onLoad={() => setShowLoading(false)}
        />
      </g>
    );
  }

  return (
    <g
      ref={setRef}
      onPointerDown={getPointerListener(selectConfiguration)}
      onMouseDown={getMouseFallbackListener(selectConfiguration)}
    >
      {isStickerSizePanelActive && (
        <StickerSizeGrid
          sizeDesign={Math.max(designSize.width, designSize.height)}
          x={x}
          y={y}
          width={width}
          height={height}
        />
      )}
      <image key={imgUrl} xlinkHref={imgUrl} x={x} y={y} width={width} height={height} />
      {isSelected && (
        <Fragment>
          <rect x={x} y={y} width={width} height={height} className="sticker__frame" />
          <ConfigurationHandle
            x={x}
            y={y + height - scaleFactor}
            size={scaleFactor}
            Icon={Delete}
            onClick={handleDelete}
          />
        </Fragment>
      )}
      {configSelectedOrOpenPanel && (
        <StickerSizeInfo container={container} designSize={designSize} />
      )}
    </g>
  );
};

interface StickerDesignRendererProps extends RendererProps<DraftDesignConfiguration> {
  appearance: Appearance;
  container?: MutableRefObject<HTMLElement | null>;
}

export const StickerDesignRenderer: FunctionComponent<StickerDesignRendererProps> = ({
  configuration,
  appearance,
  viewMap,
  isSelected,
  readOnly,
  container,
}) => {
  const imgUrl = getStickerImageUrl(configuration.design.id, {
    width: 800,
    appearanceId: appearance.id,
  });
  const { width: viewWidth, height: viewHeight } = viewMap.size;

  return readOnly ? (
    <ReadOnlySticker imgUrl={imgUrl} width={viewWidth} height={viewHeight} />
  ) : (
    <Sticker
      configuration={configuration}
      imgUrl={imgUrl}
      viewMap={viewMap}
      viewWidth={viewWidth}
      viewHeight={viewHeight}
      isSelected={isSelected}
      container={container}
    />
  );
};
